import find from 'lodash/find';
import includes from 'lodash/includes';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import unescape from 'lodash/unescape';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import uuidV4 from 'uuid/v4';
import {maps as gmaps} from 'google';
import SearchTracking from 'search-tracking';
import Icon from 'icon-component';
import SpotMap from 'search/components/common/SpotMap';
import SearchControls from './SearchControls';
import {
    getSpotsRadial,
    getSpotsTiled,
    cancelSpotRequests
} from '../../common/spots-actions';
import {
    setNewData,
    setSearchMapInstance,
    setZoom
} from '../../common/search-actions';

const EVENT_TIME_FORMAT = 'h:mm A';

class MapContainer extends Component {
    constructor(props) {
        super(props);

        this._IS_WIDGET_IFRAME = (includes(window.location.href, 'spothero') && includes(window.location.href, 'widget'));
        this._onSpotSelected = this._onSpotSelected.bind(this);
        this._onVenueSearchClick = this._onVenueSearchClick.bind(this);
        this._onNewSearch = this._onNewSearch.bind(this);
        this._onZoom = this._onZoom.bind(this);
        this._onMapMoved = this._onMapMoved.bind(this);
        this._onEventInfoClick = this._onEventInfoClick.bind(this);
        this._onShimClick = this._onShimClick.bind(this);

        this.state = {
            mapKey: uuidV4()
        };
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.latitude !== this.props.latitude || nextProps.longitude !== this.props.longitude) {
            // if the search is for a new lat/lng, trigger redraw to refresh map props
            this._triggerMapRedraw();
        }
    }

    _onEventInfoClick() {
        const {
            isSingleEvent,
            setNewSearchData
        } = this.props;

        if (!isSingleEvent) {
            setNewSearchData({
                data: {
                    currentPage: 'events'
                }
            });
        }
    }

    _onVenueSearchClick() {
        const {
            searchedDestination: {
                id
            },
            setNewSearchData
        } = this.props;

        setNewSearchData({
            data: {
                currentPage: 'events',
                showMapLink: true,
                venueId: id
            }
        });
    }

    _onNewSearch(searchData) {
        this.props.setNewSearchData({
            data: {
                venueId: null,
                eventId: null
            },
            params: searchData
        });
    }

    _onSpotSelected(spotId) {
        const {
            searchType,
            siteUrl,
            selectedEvent,
            searchString,
            spots,
            isMonthly,
            monthlyStarts,
            cancelAllSpotRequests,
            affiliate,
            operatorId
        } = this.props;

        const spot = find(spots, {spotId});
        const affiliateUrlParam = isNil(affiliate) ? '' : `&sha_affiliate=${affiliate}`;
        const operatorIdUrlParam = isNil(operatorId) ? '' : `&operator_id=${operatorId}`;
        const searchUrl = `${siteUrl}/search?kind=address&latitude=${spot.latitude}&longitude=${spot.longitude}&search_string=${searchString}&spot-id=${spotId}&referrer=widget${affiliateUrlParam}${operatorIdUrlParam}`;

        cancelAllSpotRequests();

        if (isMonthly) {
            window.open(`${searchUrl}&starts=${monthlyStarts}&monthly=true`, '_blank');
        } else {
            if (searchType === 'event') {
                window.open(`${siteUrl}/search?kind=event&id=${selectedEvent.id}&spot-id=${spotId}&referrer=widget${affiliateUrlParam}`, '_blank');
            } else {
                window.open(
                    `${searchUrl}&starts=${spot.selectedRate.starts}&ends=${spot.selectedRate.ends}`,
                    '_blank'
                );
            }
        }
    }

    _onZoom(zoom) {
        SearchTracking.updateAction('ZOOM');

        this.props.onZoom(zoom);
    }

    _onMapMoved({zoomed = false}) {
        SearchTracking.updateAction(
            (zoomed)
                ? 'ZOOM'
                : 'PAN'
        );
    }

    _onShimClick(evt) {
        const {
            onShimClick
        } = this.props;

        if (isFunction(onShimClick)) {
            onShimClick(evt);
        }
    }

    _triggerMapRedraw() {
        // regenerate a key to redraw the map because the widget doesn't do page reloading and some of the
        // core SpotMap props need to be reset
        this.setState({
            mapKey: uuidV4()
        });
    }

    render() {
        const {
            isSearchable,
            spots,
            destinationTitle,
            zoom,
            isMonthly,
            searchString,
            transientStarts,
            transientEnds,
            monthlyStarts,
            latitude,
            longitude,
            hourSpan,
            currentPage,
            searchType,
            selectedEvent,
            isSingleEvent,
            mapLoadingIncrement,
            requestIncrements,
            searchedDestination,
            onMultiOrNoResultsFound,
            loadRadialSpots,
            loadTiledSpots,
            kmlMapLayerURL,
            setSearchMap
        } = this.props;
        const kmlEnabled = (kmlMapLayerURL)
            ? true
            : (searchedDestination)
                ? searchedDestination.kml_map_layer_enabled
                : null;
        const kmlUrl = (kmlMapLayerURL)
            ? kmlMapLayerURL
            : (searchedDestination)
                ? searchedDestination.kml_map_layer_url
                : null;

        const startsTimeFormatted = (transientStarts)
            ? moment(transientStarts).subtract(2, 'h').format(EVENT_TIME_FORMAT)
            : null;
        const endsTimeFormatted = (transientEnds)
            ? moment(transientEnds).add(1, 'h').format(EVENT_TIME_FORMAT)
            : null;
        const eventInfoClasses = classNames(
            'SH-EventInfo',
            {'SH-EventInfo-disabled': isSingleEvent}
        );

        return (
            <div className="SH-MapContainer-wrapper">
                {isSearchable &&
                    <SearchControls
                        hourSpan={hourSpan}
                        isMonthly={isMonthly}
                        transientStart={transientStarts}
                        transientEnd={transientEnds}
                        monthlyStart={monthlyStarts}
                        searchString={searchString}
                        destinationData={searchedDestination}
                        onMultiOrNoResultsFound={onMultiOrNoResultsFound}
                        onNewSearch={this._onNewSearch}
                        onVenueSearchClick={this._onVenueSearchClick}
                    />
                }
                {(currentPage === 'map' && searchType === 'event' && selectedEvent) &&
                    <div
                        className={eventInfoClasses}
                    >
                        {!isSingleEvent &&
                            <Icon
                                name="chevron-left"
                                onClick={this._onEventInfoClick}
                            />
                        }
                        <div className="SH-EventInfo-desc">
                            <div className="SH-EventInfo-title">{selectedEvent.title}</div>
                            <div className="SH-EventInfo-datetime">
                                {transientStarts &&
                                    <div className="SH-EventInfo-date">{moment(transientStarts).format('ddd, MMM DD, YYYY')}</div>
                                }
                                {startsTimeFormatted &&
                                    <div className="SH-EventInfo-time">
                                        <span>{startsTimeFormatted}</span>
                                        {endsTimeFormatted &&
                                            <span> - {endsTimeFormatted}</span>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
                <div className="SH-MapContainer" ref={node => { this._container = node; }}>
                    <SpotMap
                        ref={node => { this._spotMap = node; }}
                        key={this.state.mapKey}
                        zoom={zoom}
                        onZoom={this._onZoom}
                        destination={{
                            title: destinationTitle,
                            latitude,
                            longitude,
                            position: new gmaps.LatLng(latitude, longitude)
                        }}
                        onMapMoved={this._onMapMoved}
                        spots={spots}
                        isMonthly={isMonthly}
                        searchString={searchString}
                        transientStarts={transientStarts}
                        transientEnds={transientEnds}
                        monthlyStarts={monthlyStarts}
                        mapLoadingIncrement={mapLoadingIncrement}
                        requestIncrements={requestIncrements}
                        onMarkerClick={this._onSpotSelected}
                        loadRadialSpots={loadRadialSpots}
                        loadTiledSpots={loadTiledSpots}
                        kmlMapLayerURL={kmlUrl}
                        kmlMapLayerEnabled={kmlEnabled}
                        onShimClick={this._onShimClick}
                        onGoogleMapLoaded={setSearchMap}
                    />
                </div>
            </div>
        );
    }
}

MapContainer.propTypes = {
    isSearchable: PropTypes.bool.isRequired,
    spots: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentPage: PropTypes.string.isRequired,
    searchType: PropTypes.string.isRequired,
    destinationTitle: PropTypes.string.isRequired,
    zoom: PropTypes.number.isRequired,
    isMonthly: PropTypes.bool.isRequired,
    siteUrl: PropTypes.string.isRequired,
    searchString: PropTypes.string,
    transientStarts: PropTypes.string,
    transientEnds: PropTypes.string,
    monthlyStarts: PropTypes.string,
    kmlMapLayerURL: PropTypes.string,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    operatorId: PropTypes.number,
    hourSpan: PropTypes.number.isRequired,
    selectedEvent: PropTypes.object,
    isSingleEvent: PropTypes.bool,
    mapLoadingIncrement: PropTypes.number,
    requestIncrements: PropTypes.number,
    searchedDestination: PropTypes.object,
    onMultiOrNoResultsFound: PropTypes.func.isRequired,
    loadRadialSpots: PropTypes.func.isRequired,
    loadTiledSpots: PropTypes.func.isRequired,
    setNewSearchData: PropTypes.func.isRequired,
    cancelAllSpotRequests: PropTypes.func.isRequired,
    setSearchMap: PropTypes.func,
    onZoom: PropTypes.func.isRequired,
    onShimClick: PropTypes.func,
    affiliate: PropTypes.string
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                currentPage,
                searchType,
                currentIncrement,
                requestIncrements,
                destinationTitle,
                zoom,
                searchedDestination,
                selectedEvent,
                isSingleEvent,
                params: {
                    monthly,
                    search_string,
                    starts,
                    ends,
                    monthly_start_date,
                    latitude,
                    longitude,
                    operator_id
                }
            }
        },
        spots: {
            data: spotsArray
        }
    } = state;

    return {
        spots: spotsArray,
        destinationTitle: unescape(destinationTitle),
        zoom,
        isMonthly: monthly,
        searchString: search_string,
        transientStarts: starts,
        transientEnds: ends,
        monthlyStarts: monthly_start_date,
        latitude,
        longitude,
        operatorId: operator_id,
        currentPage,
        searchType,
        mapLoadingIncrement: currentIncrement,
        searchedDestination,
        selectedEvent,
        isSingleEvent,
        requestIncrements
    };
};

const mapDispatchToProps = {
    loadRadialSpots: getSpotsRadial,
    loadTiledSpots: getSpotsTiled,
    setNewSearchData: setNewData,
    cancelAllSpotRequests: cancelSpotRequests,
    setSearchMap: setSearchMapInstance,
    onZoom: setZoom
};

export default connect(mapStateToProps, mapDispatchToProps)(MapContainer);



// WEBPACK FOOTER //
// ./widget/src/js/components/map/MapContainer.jsx